/**
 * Sprint 22 — ChatRail state.
 *
 * The cockpit chat lives in the third column on every route. This context owns:
 *  - `expanded` — persisted per-workspace (localStorage), toggled with `]`.
 *  - `scope` + `pageContext` — what the chat is "asking about", declared by the
 *    active route via {@link useChatScope}. Defaults to workspace scope.
 *  - `draft` — a prefilled composer value (e.g. Cmd-K "Ask about this passage").
 *  - `newChatNonce` — bumped to ask the panel to start a fresh session.
 *
 * Separate from RightRailContext (Sprint 9a) on purpose: that context is still
 * used by RowDetail + the command palette, so Sprint 22 adds a parallel rail
 * rather than renaming it (which would ripple through those consumers).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../../../lib/hooks/use-auth";

// Mirrors the worker's ChatScope (src/engine/chat-scope.ts). `label` is
// client-only (drives the scope pill) and is ignored by the worker.
export interface ChatScope {
  // Sprint 23 — "workforce" is the cross-agent rollup scope (Workforce route).
  // Spec №22 — "task" is the task-bound execution thread (D-CL1); it already exists
  // in the worker's ChatScope and №22.3.2 (F1) declares it from the route matrix,
  // so a conversation started on `/app/task/:id` classifies as that task's thread
  // instead of falling through to the Needs-You page scope.
  kind: "workspace" | "agent" | "entity" | "table" | "page" | "workforce" | "task";
  id?: string;
  type_slug?: string;
  label?: string;
}

export interface PageContext {
  kind: string;
  id?: string;
  name: string;
  snippet?: string;
  fields?: Record<string, unknown>;
}

const WORKSPACE_SCOPE: ChatScope = { kind: "workspace", label: "This workspace" };

export interface ChatRailContextValue {
  expanded: boolean;
  setExpanded: (value: boolean) => void;
  toggleExpanded: () => void;
  /** Active scope (what chat is asking about). Defaults to workspace. */
  scope: ChatScope;
  pageContext: PageContext | null;
  /** The scope the current route declared (drives the "this thing vs everything"
   * toggle in ScopeIndicator). Equals workspace when no route has declared one. */
  routeScope: ChatScope;
  /** Declared by the active route via useChatScope — sets route + active scope. */
  setScope: (scope: ChatScope, pageContext?: PageContext | null) => void;
  /** Reset to workspace (route unmount). */
  resetScope: () => void;
  /** User override from the scope switcher — sets the active scope only. */
  setActiveScope: (scope: ChatScope, pageContext?: PageContext | null) => void;
  /** Prefilled composer text (consumed once by the panel). */
  draft: string | null;
  setDraft: (value: string | null) => void;
  /** Bumped to request a fresh session. */
  newChatNonce: number;
  requestNewChat: () => void;
  /** Convenience for Cmd-K: expand the rail and optionally set scope/draft/new. */
  openWith: (opts?: { scope?: ChatScope; pageContext?: PageContext | null; draft?: string; newChat?: boolean }) => void;
}

const ChatRailContext = createContext<ChatRailContextValue | null>(null);

function storageKeyFor(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `kuvio_chat_rail_expanded_${workspaceId}` : null;
}

function loadExpanded(key: string | null): boolean {
  if (!key) return true; // default: expanded — chat is the cockpit's command line
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}

function persistExpanded(key: string | null, value: boolean): void {
  if (!key) return;
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function ChatRailProvider({ children }: { children: ReactNode }) {
  const { currentWorkspaceId } = useAuth();
  const key = storageKeyFor(currentWorkspaceId);

  const [expanded, setExpandedState] = useState<boolean>(() => loadExpanded(key));
  const [scope, setScopeState] = useState<ChatScope>(WORKSPACE_SCOPE);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  const [routeScope, setRouteScope] = useState<ChatScope>(WORKSPACE_SCOPE);
  const [draft, setDraft] = useState<string | null>(null);
  const [newChatNonce, setNewChatNonce] = useState(0);

  // Re-hydrate the expand preference when the workspace changes.
  useEffect(() => {
    setExpandedState(loadExpanded(key));
  }, [key]);

  const setExpanded = useCallback(
    (value: boolean) => {
      setExpandedState(value);
      persistExpanded(key, value);
    },
    [key],
  );

  const toggleExpanded = useCallback(() => {
    setExpandedState((cur) => {
      const next = !cur;
      persistExpanded(key, next);
      return next;
    });
  }, [key]);

  // Route declaration — sets the route scope AND focuses the active scope on it.
  const setScope = useCallback((next: ChatScope, ctx?: PageContext | null) => {
    setRouteScope(next);
    setScopeState(next);
    setPageContext(ctx ?? null);
  }, []);

  const resetScope = useCallback(() => {
    setRouteScope(WORKSPACE_SCOPE);
    setScopeState(WORKSPACE_SCOPE);
    setPageContext(null);
  }, []);

  // User override from the scope switcher — active scope only (route stays put).
  const setActiveScope = useCallback((next: ChatScope, ctx?: PageContext | null) => {
    setScopeState(next);
    setPageContext(ctx ?? null);
  }, []);

  const requestNewChat = useCallback(() => {
    setNewChatNonce((n) => n + 1);
  }, []);

  const openWith = useCallback<ChatRailContextValue["openWith"]>(
    (opts) => {
      setExpanded(true);
      if (opts?.scope) setScope(opts.scope, opts.pageContext ?? null);
      if (opts?.draft != null) setDraft(opts.draft);
      if (opts?.newChat) setNewChatNonce((n) => n + 1);
    },
    [setExpanded, setScope],
  );

  const value = useMemo<ChatRailContextValue>(
    () => ({
      expanded,
      setExpanded,
      toggleExpanded,
      scope,
      pageContext,
      routeScope,
      setScope,
      resetScope,
      setActiveScope,
      draft,
      setDraft,
      newChatNonce,
      requestNewChat,
      openWith,
    }),
    [expanded, setExpanded, toggleExpanded, scope, pageContext, routeScope, setScope, resetScope, setActiveScope, draft, newChatNonce, requestNewChat, openWith],
  );

  return <ChatRailContext.Provider value={value}>{children}</ChatRailContext.Provider>;
}

export function useChatRail(): ChatRailContextValue {
  const ctx = useContext(ChatRailContext);
  if (!ctx) {
    throw new Error("useChatRail must be used within a ChatRailProvider");
  }
  return ctx;
}

/**
 * №53 (53.0) re-parented the console settings pages under `/app`, whose chat
 * layer is the CoPanel — no ChatRailProvider mounts there. Consumers that only
 * DECLARE state to the rail (useChatScope) read through this accessor and no-op
 * when the rail is absent; consumers that need the rail keep useChatRail's throw.
 */
export function useChatRailOptional(): ChatRailContextValue | null {
  return useContext(ChatRailContext);
}
